import QueryString from 'qs'
import { useEffect, useMemo, useState, useRef, createContext } from 'react'
import { isMobile } from 'react-device-detect'
import TagManager, { DataLayerArgs, TagManagerArgs } from 'react-gtm-module'
import { addScriptDefault, setup } from 'meta-pixel'
import { useLocation, useNavigationType } from 'react-router-dom'
import { getProfile, postOpenid, getAccessToken } from './api/auth'
import { postLogout } from './api/login'
import { getKeywordExceptSearch } from './api/product'
import './App.css'
import Footer from './components/Footer'
import { GlobalStyle } from './components/GlobalStyle'
import Header from './components/Header'
import ChannelService from './components/myshopping/MyChannerTalk'
import { AppProvider, useAppContext } from './components/provider/AppProvider'
import { CartProvider } from './components/provider/CartProvider'
import { CommonProvider, useCommonContext } from './components/provider/CommonProvider'
import { MyProvider } from './components/provider/MyProvider'
import { PromotionProvider } from './components/provider/PromotionProvider'
import Toast from './components/toast/Toast'
import { delPrevScroll, getParsedLocalStorageItem, handleAccessToken } from './js/script'
import { KeepAliveRouteOutlet, useKeepAliveRef } from 'keepalive-for-react'
import { HelmetProvider } from 'react-helmet-async'
import usePreviousPath from './hooks/usePreviousPath'
import SEO from './SEO'
import { detect } from 'detect-browser'
import { AFWebLinkType, RNPostMessage, sendConsoleMessage } from './js/postMessage'
import { RNEventLoginUserResponse } from './@types/auth'
import { loadCremaScript } from './js/script'

export const tagManagerArgs: TagManagerArgs = {
  gtmId: `${process.env.REACT_APP_GTM_ID}`, // Google Tag Manager ID
}

export const metaPixelArgs = {
  pixelId: `${process.env.REACT_APP_META_PIXEL_ID}`, // Meta Pixel ID
}

export const PathContext = createContext<any>(undefined)

function App() {
  const [productPage, setProductPage] = useState<boolean>(false)
  const [sanctions, setsanctions] = useState<boolean>(false)
  const [data, setData] = useState(null)
  const [errorPage, setErrorPage] = useState<boolean>(false)
  const [page, setPage] = useState<number>(1)
  const [expiredReady, setExpiredReady] = useState<boolean | null>(null)
  const [allSearch, setAllSearch] = useState<any>([])
  const { isAppView } = useAppContext()
  const { hasAccessToken, setHasAccessToken } = useCommonContext()
  const keepAliveRef = useRef<HTMLDivElement>(null)
  const aliveRef = useKeepAliveRef()
  const previousPath = usePreviousPath()
  const navigationType = useNavigationType()
  const location = useLocation()
  const pathname = location.pathname
  const channelService = new ChannelService()
  const fbq = addScriptDefault()

  // 스크롤 훅 로드 - 페이지 이동 시 해당 훅이 스크롤 위치 저장 및 기본 좌표로 이동 시킴
  // useScrollRestore();

  const isHideHeader = useMemo(() => {
    return (
      (productPage && isMobile) ||
      sanctions ||
      errorPage ||
      location.pathname.includes('mypaypaid') ||
      location.pathname.includes('myPayReady') ||
      location.pathname.includes('addcard') ||
      location.pathname.includes('oauth')
    )
  }, [productPage, isMobile, sanctions, errorPage, location.pathname])

  const isHideFooter = useMemo(() => {
    return (
      sanctions ||
      errorPage ||
      location.pathname.includes('mypaypaid') ||
      location.pathname.includes('close') ||
      location.pathname.includes('myPayReady') ||
      location.pathname.includes('addcard') ||
      location.pathname.includes('oauth')
    )
  }, [sanctions, errorPage, location.pathname])

  //gtm
  useEffect(() => {
    const accessToken = getAccessToken()
    if (accessToken || hasAccessToken) {
      getProfile().then((res) => {
        if (!isAppView) {
          // 웹 사용자인 경우 GTM DataLayer 이벤트..
          const setGTMUserId: DataLayerArgs = {
            dataLayer: {
              userId: `${res.data.memberId}`, // 사용자 구분을 위한 값. 수집이 안되는 경우 user_id 로 적용
              // userProject: `${사용자속성}`, // 옵셔널: user properties 속성 예시
            },
          }
          TagManager.dataLayer(setGTMUserId)
          TagManager.initialize(tagManagerArgs)
        }
      })
    }

    // meta pixel settings
    setup(fbq).init(metaPixelArgs.pixelId, true)
  }, [])

  useEffect(() => {
    channelService.hideMessenger() // 경로 변경 시 Channel Talk 숨기기
  }, [pathname])

  useEffect(() => {
    // 크리마 스크립트 로드
    // loadCremaScript()

    // location 변경 시 meta pixel pageview 이벤트 전송
    fbq('track', 'PageView')

    // localStorage 변경을 감지하는 이벤트 리스너
    const handleStorageChange = (e: any) => {
      if (e.key === 'aboutAccessToken') setData(e.newValue || '')
    }
    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [location])

  // 어바웃피싱 토큰 undefined일때 토큰 삭제
  useEffect(() => {
    const aboutToken = window.localStorage.getItem('aboutAccessToken')
    if (aboutToken === undefined) {
      window.localStorage.removeItem('aboutAccessToken')
      window.localStorage.removeItem('aboutRefreshToken')
      window.localStorage.removeItem('accessToken')
      window.location.reload()
    }
  }, [data])

  // 어바웃피싱 토큰 undefined일때 토큰 삭제
  useEffect(() => {
    const query = QueryString.parse(location.search, {
      ignoreQueryPrefix: true,
    })
    const aboutToken = QueryString.parse(location.search.split('/?')[1], {
      ignoreQueryPrefix: true,
    })
    if (query.app && ((!query.token && query.token === '') || (!aboutToken.token && aboutToken.token === ''))) {
      window.localStorage.clear()
      setHasAccessToken(false)
    }

    if (query.app) {
      //@ts-ignore
      window.sessionStorage.setItem('app', query.app)
    }
    if (query.token) {
      window.localStorage.setItem('aboutAccessToken', JSON.stringify(query.token))
      //@ts-ignore
      postOpenid(query.token).then((res) => {
        window.localStorage.setItem(`accessToken`, JSON.stringify(res.data.accessToken))
        retainDirectParameterAndReload()
      })
    }
    if (aboutToken.token) {
      window.localStorage.setItem('aboutAccessToken', JSON.stringify(aboutToken.token))
      //@ts-ignore
      postOpenid(aboutToken.token).then((res) => {
        window.localStorage.setItem(`accessToken`, JSON.stringify(res.data.accessToken))
        retainDirectParameterAndReload()
      })
    }
  }, [location])

  const retainDirectParameterAndReload = () => {
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)

    // direct 파라미터를 가져옵니다.
    const directValue = params.get('direct')

    // 모든 파라미터를 제거합니다.
    params.forEach((value, key) => {
      if (key !== 'direct') {
        params.delete(key)
      }
    })

    // direct 파라미터를 다시 추가합니다.
    if (directValue !== null) {
      params.set('direct', directValue)
    }

    // 변경된 URL을 생성합니다.
    const newUrl = `${url.origin}${url.pathname}?${params.toString()}`

    // URL만 변경하고 페이지를 새로고침하지 않습니다.
    window.history.replaceState(null, '', newUrl)
    setHasAccessToken(true)
  }

  useEffect(() => {
    const fnExpiredTime = async () => {
      const expiredTimeValue = new URLSearchParams(window.location.search).get('expiredTime')
      if (expiredTimeValue !== null && expiredTimeValue !== null && expiredTimeValue !== '0') {
        window.sessionStorage.setItem('expiredTime', expiredTimeValue)
        setExpiredReady(true)
      }
      setExpiredReady(false)
    }
    fnExpiredTime()
  }, [])

  useEffect(() => {
    const cart = getParsedLocalStorageItem('cartlist')
    if (cart === null) {
      window.localStorage.setItem('cartlist', JSON.stringify([]))
    }

    if (keepAliveRef.current) keepAliveRef.current.style.height = 'auto'
  }, [])

  // 앱에서 로그아웃 시켜버리기
  useEffect(() => {
    if (expiredReady !== null && isAppView) {
      const expiredTimeValue = new URLSearchParams(window.location.search).get('expiredTime')
      // expiredTimeValue는 파라미터 자체가 없을때는 null, 값이 없을때는 빈값이므로, 값이 있지만 0인 상태만 확인
      if (expiredTimeValue === '0') {
        getProfile()
          .then((res) => {
            postLogout(Number(res.data.memberId))
              .then(() => {
                window.localStorage.removeItem('aboutRefreshToken')
                window.localStorage.removeItem('aboutAccessToken')
                window.localStorage.removeItem('accessToken')
                window.localStorage.removeItem('lastAccessTokenUpdateTime')
                delPrevScroll()
                window.sessionStorage.removeItem('expiredTime')
                setHasAccessToken(false)
                window.cremaAsyncInit = function () {
                  window.crema.init(null, null) // 크리마 로그아웃
                }

                // 로그인을 하지 않은 상태이면서, 로그인 정보가 아직 남아있다면 새로고침
                if (res.data.memberId) {
                  window.location.reload()
                }
              })
              .catch((err) => {
                console.log(err)
              })
          })
          .catch((err) => console.log(err))
      }
    }
  }, [expiredReady, isAppView])

  useEffect(() => {
    if (location.pathname.includes('/product/')) {
      setProductPage(true)
    } else {
      setProductPage(false)
    }
  }, [location.pathname])

  useEffect(() => {
    if (location.pathname.includes('sanctions')) {
      setsanctions(true)
    } else {
      setsanctions(false)
    }
  }, [location.pathname])

  useEffect(() => {
    const browerOs = detect()

    const refreshToken = getParsedLocalStorageItem(`aboutRefreshToken`)
    if (browerOs?.os && refreshToken) {
      if (!location.pathname.includes('mypaypaid')) {
        handleAccessToken(refreshToken, browerOs?.os)
      }
    }
  }, [location.pathname])

  useEffect(() => {
    getKeywordExceptSearch('POPULAR', page, '', '787334')
      .then((res) => {
        setAllSearch((prev: any) => [...prev, ...res.data.items])
        setPage(2)
      })
      .catch((err) => console.log(err))
  }, [page])

  useEffect(() => {
    const aboutFishing = window.localStorage.getItem('aboutAccessToken')
    if (aboutFishing === undefined) {
      window.localStorage.removeItem('aboutAccessToken')
      window.localStorage.removeItem('aboutRefreshToken')
      window.localStorage.removeItem('accessToken')
      window.location.reload()
    }
  }, [location])

  useEffect(() => {
    // POP이 아닐 때는 캐시된 컴포넌트 새로고침
    if (!isAppView && navigationType !== 'POP') {
      aliveRef.current?.refresh()
    }

    // 페이지 이동 시 크리마 팝업 종료
    // const popupIframes = document.querySelectorAll(
    //   '#crema-review-popup, #crema-fullscreen-popup'
    // ) as NodeListOf<HTMLIFrameElement>
    // if (!isAppView && popupIframes.length > 0) {
    //   popupIframes.forEach((el) => {
    //     if (el.parentNode) el.parentNode.removeChild(el)
    //   })
    // }
  }, [location])

  useEffect(() => {
    RNPostMessage(AFWebLinkType.onVisibleTopNavigationChange, { eventType: false })
  }, [location])

  useEffect(() => {
    RNPostMessage(AFWebLinkType.getUserData)

    const handleLoginUserEvent = (event: any) => {
      if (!hasAccessToken) {
        const isAboutAccessToken = window.localStorage.getItem('aboutAccessToken')
        const isAccessToken = window.localStorage.getItem('accessToken')
        if (!isAboutAccessToken || !isAccessToken) {
          window.localStorage.setItem('aboutAccessToken', JSON.stringify(event.data.accessToken))
          postOpenid(event.data.accessToken).then((res) => {
            window.localStorage.setItem(`accessToken`, JSON.stringify(res.data.accessToken))
            setHasAccessToken(true)
            retainDirectParameterAndReload()
          })
        }
      }
    }

    window.addEventListener('loginUser', (event) => handleLoginUserEvent(event))
  }, [])

  return (
    <>
      <GlobalStyle />
      <HelmetProvider>
        {!location.pathname.includes('product') && !location.pathname.includes('exhibitiondetail') && (
          <SEO title='어바웃피싱(aboutfishing)' image={`${process.env.PUBLIC_URL}/ogimage.png`} />
        )}

        <MyProvider>
          <AppProvider>
            <CommonProvider>
              <CartProvider>
                <PromotionProvider>
                  <PathContext.Provider value={{ path: previousPath, allSearch }}>
                    {!isHideHeader && <Header></Header>}
                    <div className='outletWrap'>
                      <KeepAliveRouteOutlet
                        customContainerRef={keepAliveRef}
                        aliveRef={aliveRef}
                      ></KeepAliveRouteOutlet>
                    </div>

                    {/* <Outlet /> */}
                    {!isHideFooter && <Footer></Footer>}
                    <Toast></Toast>
                  </PathContext.Provider>
                </PromotionProvider>
              </CartProvider>
            </CommonProvider>
          </AppProvider>
        </MyProvider>
      </HelmetProvider>
    </>
  )
}

export default App
